import { useEffect, useRef, useState } from 'react';

// Third Party Integrations
import TemplateBuilderSDK from '@open-letter-marketing/olc-react-sdk';

// Hooks
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

// Actions
import {
  createPhoneTrackingNumber,
  fetchPhoneTrackingNumbers,
  lookupPhoneNumbers,
} from '~/redux/actions/phone-tracking-actions';
import {
  createTemplate,
  getTemplatesByTab,
  getOneTemplate,
  updateTemplate,
  acceptTemplateProof,
  denyTemplateProof,
  doublicateTemplate,
  fetchAllBrandingImages,
  deleteImageFromBranding,
  uploadImageToBranding,
  fetchAllQR,
  uploadQR,
  updateQR,
  deleteQR,
  updateQRStatus,
  getAllProducts,
  preflightHtmlV2Template,
  viewProofHtmlV2Template,
  generateProofImagesHtmlV2Template,
  uploadTemplateImage,
} from '~/redux/actions/template-builder';
import { createCustomField, fetchCustomFieldsCatalog } from '~/redux/actions/customFields-actions';
import { fetchContactsForTemplatePreview } from '~/redux/actions/contacts-actions';
import { failure, success } from '~/redux/actions/snackbar-actions';
import {
  fetchDesignerQueryDetails,
  fetchDesignerQueryTemplateDetails,
} from '~/redux/actions/custom-template-query-actions';
import {
  getDesignerTemplateQueryDetails,
  getOneDesignerTemplateQuery,
} from '~/redux/actions/admin-custom-designer-query';
import PurchaseDialog from '~/components/HireDesigner/PurchaseDialog';
import ChargeDialog from '~/components/HireDesigner/ChargeDialog';
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';
import Loader from '~/components/General/Loader';
import CreateContactModal from '~/components/Contacts/CreateContactModal';
import { updateAdminQueryStatus } from '../../redux/actions/admin-query-actions';

// Utils
import { getUserToken } from '~/utils/auth';
import { getDynamicImageProxyUrl } from '~/utils/dynamicImageProxy';
import { MESSAGES } from '~/utils/message';
import { installDefaultTemplateName } from './templateName';
import { mapContactToPreviewContact, normalizePreviewSearch } from './templatePreviewContacts';
import { installRealPennedFieldsDefault, installSidePanelTabIdentity } from './realPennedDefaults';
import { DESIGNER_QUERY_PROOF_PREVIEW_STATUSES, ENGAGEMENT_TYPES } from '~/utils/constants';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { CircularProgress, InputLabel, TextField } from '@mui/material';

// Componennts
import DuplicateConfirmModal from '../Templates/DuplicateModal/DuplicateConfrimModal';
import TemplateGalleryDropdownBridge from './TemplateGalleryDropdownBridge';
import './v2Theme.scss';

const isDesignerQueryProofPreviewable = (status = '') =>
  DESIGNER_QUERY_PROOF_PREVIEW_STATUSES.includes(status);

const getFirstValue = (source, keys) => {
  const sources = [
    source,
    source?.proof,
    source?.proofApproval,
    source?.proofRevision,
    source?.revision,
    source?.meta,
  ];

  for (const currentSource of sources) {
    if (!currentSource || typeof currentSource !== 'object') {
      continue;
    }

    for (const key of keys) {
      const value = currentSource[key];

      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
  }

  return '';
};

const getProofRevision = (template = {}) => {
  const currentRevision = getFirstValue(template, [
    'proofRevision',
    'proof_revision',
    'currentProofRevision',
    'current_proof_revision',
    'currentRevision',
    'current_revision',
    'revisionNumber',
    'revision_number',
    'revisionNo',
    'revision_no',
  ]);
  const totalRevisions = getFirstValue(template, [
    'totalProofRevisions',
    'total_proof_revisions',
    'totalRevisions',
    'total_revisions',
    'revisionTotal',
    'revision_total',
    'maxRevisions',
    'max_revisions',
  ]);

  if (currentRevision && totalRevisions) {
    return {
      currentRevision,
      totalRevision: totalRevisions,
    };
  }

  return currentRevision || '';
};

const getDesignerQueryProofRevision = (query = {}) => {
  const explicitRevision = getProofRevision(query);

  if (explicitRevision) {
    return explicitRevision;
  }

  const revisionsCount = query?.revisionsCount ?? query?.rejectionCount;
  const totalRevisions =
    query?.totalRevisions ?? query?.maxRevisions ?? query?.total_revisions ?? 3;

  if (revisionsCount !== null && revisionsCount !== undefined) {
    return {
      currentRevision: Number(revisionsCount || 0) + 1,
      totalRevision: totalRevisions,
    };
  }

  return '';
};

const getDesignerQueryId = (template = {}, fallbackId = '') =>
  getFirstValue(template, [
    'id',
    'customTemplateQueryId',
    'custom_template_query_id',
    'templateQueryId',
    'template_query_id',
    'queryId',
    'query_id',
    'designerQueryId',
    'designer_query_id',
  ]) || fallbackId;

const getDownloadUrl = (path) => {
  if (!path || /^https?:\/\//i.test(path)) {
    return path || '';
  }

  const baseUrl = String(import.meta.env.VITE_APP_BASE_URL || '').replace(/\/$/, '');
  const normalizedPath = String(path).replace(/^\//, '');

  return `${baseUrl}/api/v1/download/${normalizedPath}`;
};

const getLatestFileHistory = (fileHistory = []) => {
  if (!Array.isArray(fileHistory) || !fileHistory.length) {
    return {};
  }

  return (
    [...fileHistory].sort((a, b) => {
      const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);

      if (versionDiff !== 0) {
        return versionDiff;
      }

      return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
    })[0] || {}
  );
};

const findProductById = (products = [], productId) => {
  if (!productId) {
    return null;
  }

  for (const product of products) {
    if (String(product.id) === String(productId)) {
      return product;
    }

    const matchingSize = product?.size?.find?.(
      (size) => String(size.id || product.id) === String(productId)
    );

    if (matchingSize) {
      return {
        ...product,
        id: matchingSize.id || product.id,
        paperSize: matchingSize.paperSize || matchingSize.size || product.paperSize,
        size: [matchingSize],
      };
    }
  }

  return null;
};

const mergeDesignerQueryProduct = (apiProduct, fallbackProduct) => {
  if (!apiProduct && !fallbackProduct) {
    return null;
  }

  return {
    ...(fallbackProduct || {}),
    ...(apiProduct || {}),
    paperSize: apiProduct?.paperSize || apiProduct?.selectedSize || fallbackProduct?.paperSize,
    selectedSize:
      apiProduct?.selectedSize ||
      apiProduct?.paperSize ||
      fallbackProduct?.selectedSize ||
      fallbackProduct?.paperSize,
  };
};

const getTemplateDataFromDesignerQuery = (query = {}, products = []) => {
  const meta = query?.meta || {};
  const template = query?.template || query?.sourceTemplate || {};
  const latestFileHistory = getLatestFileHistory(query?.fileHistory);
  const productId =
    template.productId ||
    template.product_id ||
    query.productId ||
    query.product_id ||
    template?.product?.id ||
    query?.product?.id;
  const resolvedProduct = findProductById(products, productId);
  const product = mergeDesignerQueryProduct(query.product || template.product, resolvedProduct);
  const templateUrl = getDownloadUrl(
    latestFileHistory.afterTemplateUrl ||
      latestFileHistory.afterTemplatePath ||
      meta.templateUrl ||
      meta.templatePath ||
      template.templateUrl ||
      template.templatePath
  );
  const thumbnailUrl = getDownloadUrl(
    latestFileHistory.afterThumbnailUrl ||
      latestFileHistory.afterThumbnailPath ||
      meta.thumbnailUrl ||
      meta.thumbnailPath ||
      template.thumbnailUrl ||
      template.thumbnailPath
  );

  if (!templateUrl) {
    return null;
  }

  return {
    ...template,
    title: template.title || query?.meta?.templateTitle || query?.title,
    templateUrl,
    backTemplateUrl: getDownloadUrl(
      latestFileHistory.afterBackTemplateUrl ||
        latestFileHistory.afterBackTemplatePath ||
        meta.backTemplateUrl ||
        meta.backTemplatePath ||
        template.backTemplateUrl ||
        template.backTemplatePath
    ),
    thumbnailUrl,
    backThumbnailUrl: getDownloadUrl(
      latestFileHistory.afterBackThumbnailUrl ||
        latestFileHistory.afterBackThumbnailPath ||
        meta.backThumbnailUrl ||
        meta.backThumbnailPath ||
        template.backThumbnailUrl ||
        template.backThumbnailPath
    ),
    product,
    id:
      template.id ||
      query.sourceTemplateId ||
      query.source_template_id ||
      query.templateId ||
      query.template_id ||
      query.id,
    sourceTemplateId:
      query.sourceTemplateId || query.source_template_id || query.sourceTemplate?.id,
    templateId: query.templateId || query.template_id,
    customTemplateQueryId: query.id,
    templateQueryId: query.id,
    rejectionCount: query.revisionsCount ?? query.rejectionCount,
    proofRevision: getDesignerQueryProofRevision(query),
    revision: query.revision,
    meta: {
      ...(template.meta || {}),
      ...meta,
      templatePath: meta.templatePath || template.templatePath,
      thumbnailPath: meta.thumbnailPath || template.thumbnailPath,
      backTemplatePath: meta.backTemplatePath || template.backTemplatePath,
      backThumbnailPath: meta.backThumbnailPath || template.backThumbnailPath,
    },
    designerQuery: query,
  };
};

const getTemplateDataFromDesignerQueryDetails = (queryDetails = {}, query = {}, products = []) => {
  const template = queryDetails?.template || {};

  if (!template?.templateUrl) {
    return null;
  }

  const productId = template?.product?.id || query?.productId || query?.product_id;
  const resolvedProduct = findProductById(products, productId);
  const product = mergeDesignerQueryProduct(template.product || query.product, resolvedProduct);

  return {
    ...template,
    title: template.title || query?.meta?.templateTitle || query?.title,
    templateUrl: getDownloadUrl(template.templateUrl),
    backTemplateUrl: getDownloadUrl(template.backTemplateUrl),
    thumbnailUrl: getDownloadUrl(template.thumbnailUrl),
    backThumbnailUrl: getDownloadUrl(template.backThumbnailUrl),
    product,
    id:
      template.id ||
      query.sourceTemplateId ||
      query.source_template_id ||
      query.templateId ||
      query.template_id ||
      queryDetails.id ||
      query.id,
    sourceTemplateId:
      query.sourceTemplateId || query.source_template_id || query.sourceTemplate?.id,
    templateId: query.templateId || query.template_id,
    customTemplateQueryId: queryDetails.id || query.id,
    templateQueryId: queryDetails.id || query.id,
    rejectionCount: query.revisionsCount ?? query.rejectionCount,
    proofRevision: getDesignerQueryProofRevision(query),
    revision: query.revision,
    meta: {
      ...(template.meta || {}),
      ...(query.meta || {}),
    },
    designerQuery: query,
  };
};

const getTemplateIdFromDesignerQuery = (query = {}) =>
  query?.template?.id ||
  query?.sourceTemplate?.id ||
  query?.templateId ||
  query?.template_id ||
  query?.sourceTemplateId ||
  query?.source_template_id ||
  '';

const getProofApprovalData = (query = {}) => {
  if (!query?.id) {
    return undefined;
  }

  return {
    status: query.status,
    isDeniable: query.isDeniable ?? query.is_deniable,
    sourceTemplateId:
      query.sourceTemplateId ?? query.source_template_id ?? query.sourceTemplate?.id,
    revisionsCount: query.revisionsCount ?? query.rejectionCount,
    lastAction: query.lastAction,
  };
};

const isSuccessfulResponse = (response) => response?.status >= 200 && response?.status < 300;

const TemplateBuilder = ({ approvalMode = false, builderMode = 'drag-drop' }) => {
  let { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isCustomTemplate = approvalMode || queryParams.get('custom-template') === 'true';

  const templateBuilderInstanceRef = useRef(null);
  const isSandboxMode = import.meta.env.VITE_APP_ENV !== 'production' ? true : false;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentTheme = 'v2';

  const isPropertyAddressEnabled = useSelector(
    (state) =>
      state?.generalSettingsReducers?.generalSettings?.propertyAddressStrictness === 'Include' ||
      false
  );

  const designerQueryAmount =
    useSelector(
      (state) => state.userReducers?.user?.organization?.systemConfig?.designerQueryAmount
    ) || null;
  const userEmail = useSelector((state) => state.userReducers?.user?.email || '');
  const isQrScanNotificationEnabled = useSelector(
    (state) => state.userReducers?.user?.subscribedPlan?.isDefault === false
  );
  const products = useSelector((state) => state.templateReducer.products);
  const customFieldsCatalog = useSelector((state) => state.customFieldsReducers?.allCustomFields);
  const isSystemAdmin = useSelector((state) => state.userReducers.isSystemAdmin);
  const isSystemSuperAdmin = useSelector((state) => state.userReducers.user?.isSystemSuperAdmin);

  const [isChargeDialogOpen, setIsChargeDialogOpen] = useState(false);
  const [designerQueryPayload, setDesignerQueryPayload] = useState({});
  const [isPurchasedOpen, setIsPurchasedOpen] = useState(false);
  const [confirmDuplicatModal, setConfirmDuplicatModal] = useState(false);
  const [duplicateTemplateId, setDuplicateTemplateId] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isSaveModalLoading, setIsSaveModalLoading] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isCloneModalLoading, setIsCloneModalLoading] = useState(false);
  const [cloneTemplateName, setCloneTemplateName] = useState('');
  const [isReuseModalOpen, setIsReuseModalOpen] = useState(false);
  const [isReuseModalLoading, setIsReuseModalLoading] = useState(false);
  const [isAddPreviewContactOpen, setIsAddPreviewContactOpen] = useState(false);
  // The SDK keeps the callbacks it was created with, so values they read live in refs.
  const previewContactOptionsRef = useRef({});
  const addPreviewContactResolverRef = useRef(null);
  const allowSdkSaveClickRef = useRef(false);
  const sdkSaveBridgeActiveRef = useRef(false);
  const sdkSavePreflightActiveRef = useRef(false);
  const sdkSaveTimeoutRef = useRef(null);
  const sdkSavePreflightTimeoutRef = useRef(null);
  const sdkReuseTimeoutRef = useRef(null);
  const [designerQueryDetails, setDesignerQueryDetails] = useState(null);
  const [isTemplateBuilderLoading, setIsTemplateBuilderLoading] = useState(true);

  const redirectToOlc = () => {
    navigate(approvalMode ? '/hire-a-designer' : '/templates');
  };

  const onCreate = async (formData) => {
    try {
      const { data, status } = await createTemplate(formData);
      if (status === 200) {
        dispatch(success(MESSAGES.TEMPLATE.TEMPLATE_BUILDER.CREATED));
        return true;
      }
    } catch (error) {
      const errorMessage =
        error?.data?.data?.errors?.title?.[0] ||
        error?.data?.data?.errors?.[0]?.msg ||
        error?.data?.message ||
        'Internal Server Error';
      dispatch(failure(errorMessage));
      return false;
    }
  };

  const onUpdate = async (formData, currentDesignerQuery = designerQueryDetails) => {
    try {
      const templateId = isCustomTemplate
        ? getTemplateIdFromDesignerQuery(currentDesignerQuery)
        : id;

      if (isCustomTemplate && !templateId) {
        dispatch(failure('Please accept or deny the proof to continue.'));
        return false;
      }

      if (!templateId) {
        dispatch(failure('Template is required to update proof.'));
        return false;
      }

      const { data, status } = await updateTemplate(templateId, formData);
      if (isCustomTemplate) {
        const queryPayload = {
          id,
          templatePath: data?.data?.templateUrl,
          thumbnailPath: data?.data?.thumbnailUrl,
          status: 'In progress',
        };
        await updateAdminQueryStatus(queryPayload);
      }
      if (status === 200) {
        dispatch(success(MESSAGES.TEMPLATE.TEMPLATE_BUILDER.UPDATED));
        return true;
      }
    } catch (error) {
      const errorMessage =
        error?.data?.data?.errors?.title?.[0] ||
        error?.data?.data?.errors?.[0]?.msg ||
        error?.data?.message ||
        'Internal Server Error';
      dispatch(failure(errorMessage));
      return false;
    }
  };

  const setEditingTemplate = async () => {
    try {
      setIsTemplateBuilderLoading(true);
      const template = await getOneTemplate(id);
      if (template?.data?.data) {
        setDesignerQueryDetails(null);
        createTemplateBuilder(template?.data?.data);
      } else {
        redirectToOlc();
      }
    } finally {
      setIsTemplateBuilderLoading(false);
    }
  };

  const setEditingDesignerQueryTemplate = async () => {
    try {
      setIsTemplateBuilderLoading(true);
      const shouldUseAdminDesignerQueryApi =
        !approvalMode && Boolean(isSystemAdmin || isSystemSuperAdmin);
      let response = null;
      let templateDetailsResponse = null;

      if (shouldUseAdminDesignerQueryApi) {
        [response, templateDetailsResponse] = await Promise.all([
          getDesignerTemplateQueryDetails(id),
          getOneDesignerTemplateQuery(id),
        ]);
      } else if (approvalMode) {
        response = await fetchDesignerQueryDetails(id);
      } else {
        [response, templateDetailsResponse] = await Promise.all([
          fetchDesignerQueryDetails(id),
          fetchDesignerQueryTemplateDetails(id).catch(() => null),
        ]);
      }

      if (
        !approvalMode &&
        (!isSuccessfulResponse(response) || !isSuccessfulResponse(templateDetailsResponse))
      ) {
        const fallbackResponse = shouldUseAdminDesignerQueryApi
          ? await fetchDesignerQueryDetails(id)
          : await getDesignerTemplateQueryDetails(id);
        const fallbackTemplateDetailsResponse = shouldUseAdminDesignerQueryApi
          ? await fetchDesignerQueryTemplateDetails(id).catch(() => null)
          : await getOneDesignerTemplateQuery(id);

        if (isSuccessfulResponse(fallbackResponse)) {
          response = fallbackResponse;
        }

        if (isSuccessfulResponse(fallbackTemplateDetailsResponse)) {
          templateDetailsResponse = fallbackTemplateDetailsResponse;
        }
      }

      const designerQuery = isSuccessfulResponse(response) ? response?.data?.data || {} : {};
      const templateDetails = templateDetailsResponse?.data?.data;
      let productOptions = products;

      if (!productOptions?.length) {
        const productsResponse = await dispatch(getAllProducts());
        productOptions = productsResponse?.data?.data || [];
      }

      const queryDetails = {
        ...designerQuery,
        id: designerQuery?.id || templateDetails?.id || id,
        ownerEmail: templateDetails?.ownerEmail || designerQuery?.email,
        isDefaultPlan: templateDetails?.isDefaultPlan,
        isUpdated: templateDetails?.isUpdated,
      };

      if (
        approvalMode &&
        queryDetails?.status &&
        !isDesignerQueryProofPreviewable(queryDetails?.status)
      ) {
        dispatch(failure('This design is not available for proof preview.'));
        redirectToOlc();
        return;
      }

      const template =
        getTemplateDataFromDesignerQueryDetails(templateDetails, queryDetails, productOptions) ||
        getTemplateDataFromDesignerQuery(queryDetails, productOptions);

      if (isSuccessfulResponse(response) && template) {
        setDesignerQueryDetails(queryDetails);
        createTemplateBuilder(template, queryDetails);
      } else {
        dispatch(failure(response?.data?.message || 'Unable to load designer query.'));
        redirectToOlc();
      }
    } finally {
      setIsTemplateBuilderLoading(false);
    }
  };

  const getAllTemplatesByTab = async (payload) => {
    try {
      const tempates = await getTemplatesByTab(payload);
      if (tempates.status === 200) {
        return tempates?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const getTemplateById = async (id) => {
    try {
      const tempate = await getOneTemplate(id);
      if (tempate.status === 200) {
        return tempate?.data?.data;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return false;
    }
  };

  const preflightHtmlTemplate = async (payload) => {
    const response = await preflightHtmlV2Template(payload);
    if (response?.status && response.status !== 200) {
      return response;
    }
    return response;
  };

  const viewProofHtmlTemplate = async (payload) => {
    // The SDK shows the failure message for proofs - don't also toast it here.
    return viewProofHtmlV2Template(payload);
  };

  const generateProofImagesHtmlTemplate = async (payload) => {
    // The SDK shows the failure message for proofs - don't also toast it here.
    return generateProofImagesHtmlV2Template(payload);
  };

  const uploadHtmlTemplateImage = async (payload) => {
    const imageUrl = await uploadTemplateImage(payload);
    if (!imageUrl) {
      dispatch(failure('Image upload failed. Please try again.'));
    }
    return imageUrl;
  };

  const submitHtmlTemplate = async (formData, context = {}) => {
    try {
      if (context.mode === 'update' && context.id) {
        const response = await updateTemplate(context.id, formData);
        if (response?.status === 200) {
          return response;
        }
        dispatch(failure(response?.data?.message || 'Template update failed'));
        return null;
      }

      const response = await createTemplate(formData);
      if (response?.status === 200 || response?.status === 201) {
        return response;
      }

      dispatch(failure(response?.data?.message || 'Template create failed'));
      return null;
    } catch (error) {
      const errorMessage =
        error?.data?.data?.errors?.title?.[0] ||
        error?.data?.data?.errors?.[0]?.msg ||
        error?.data?.message ||
        'Internal Server Error';
      dispatch(failure(errorMessage));
      return null;
    }
  };

  previewContactOptionsRef.current = {
    customFieldsCatalog: customFieldsCatalog || [],
    includePropertyFields: isPropertyAddressEnabled,
  };

  const getPreviewContacts = async ({ page, pageSize, search }) => {
    const response = await dispatch(
      fetchContactsForTemplatePreview({ page, pageSize, search: normalizePreviewSearch(search) })
    );

    if (response?.status !== 200) {
      throw new Error(response?.data?.message || 'Unable to load contacts.');
    }

    const { rows = [], count } = response.data?.data || {};
    return {
      rows: rows.map((contact) =>
        mapContactToPreviewContact(contact, previewContactOptionsRef.current)
      ),
      count,
    };
  };

  // Resolves with the newly created contact, or null if the user dismisses the modal.
  const addPreviewContact = () =>
    new Promise((resolve) => {
      addPreviewContactResolverRef.current = resolve;
      setIsAddPreviewContactOpen(true);
    });

  const resolveAddPreviewContact = (contact) => {
    addPreviewContactResolverRef.current?.(contact);
    addPreviewContactResolverRef.current = null;
  };

  const getCustomFields = async () => {
    const customFields = await dispatch(fetchCustomFieldsCatalog());
    return customFields?.data?.data?.rows || [];
  };

  const createBuilderCustomField = async (payload) => {
    try {
      const response = await dispatch(createCustomField(payload, { page: 1, pageSize: 20 }));

      if (response?.status === 200 || response?.status === 201) {
        dispatch(success(response?.data?.message || 'Custom field created successfully.'));
      } else if (response?.data?.message) {
        dispatch(failure(response.data.message));
      }

      if (
        (response?.status === 200 || response?.status === 201) &&
        !response?.data?.data?.id &&
        payload?.customFieldName
      ) {
        const catalogResponse = await dispatch(fetchCustomFieldsCatalog());
        const normalizedPayloadName = payload.customFieldName
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '_');
        const createdCustomField = catalogResponse?.data?.data?.rows?.find((field) => {
          const fieldName = (field?.value || field?.label || field?.name || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_');
          return field?.type === payload.type && fieldName === normalizedPayloadName;
        });

        if (createdCustomField?.id) {
          return {
            ...response,
            data: {
              ...response.data,
              data: createdCustomField,
            },
          };
        }
      }

      return response;
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || 'Internal Server Error'));
      return error?.response || error;
    }
  };

  const getAllBrandingImages = async (payload) => {
    try {
      const images = await fetchAllBrandingImages(payload);
      if (images.status === 200) {
        return images?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const deleteBrandingImage = async (payload) => {
    try {
      const images = await deleteImageFromBranding(payload);
      if (images.status === 200) {
        dispatch(success(images?.data?.message || 'Branding Imaage deleted successfully.'));
        return images?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const uploadBrandingImaage = async (payload) => {
    try {
      const images = await uploadImageToBranding(payload);
      if (images.status === 200) {
        return images?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const getAllQRCodes = async (payload) => {
    try {
      const qr = await fetchAllQR(payload);
      if (qr.status === 200) {
        return qr?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const getAllPurls = (payload) =>
    getAllQRCodes({
      ...payload,
      engagementType: ENGAGEMENT_TYPES.PURL,
    });

  const deleteQRCode = async (payload) => {
    try {
      const qr = await deleteQR(payload);
      if (qr.status === 200) {
        dispatch(success(qr?.data?.message || 'QR Code deleted successfully.'));
        return qr?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const uploadQRCode = async (payload) => {
    try {
      const qr = await uploadQR(payload);
      if (qr.status === 200 || qr.status === 201) {
        dispatch(success(qr?.data?.message || 'QR Code created successfully.'));
        return qr?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const createPurl = async (payload) => {
    return uploadQR({
      ...payload,
      engagementType: ENGAGEMENT_TYPES.PURL,
    });
  };

  const updatePurl = async (payload) => {
    return updateQR({
      ...payload,
      engagementType: ENGAGEMENT_TYPES.PURL,
    });
  };

  const updatePurlStatus = async (id, status) => {
    return updateQRStatus({ id, status });
  };

  const updateQRCode = async (payload) => {
    try {
      const qr = await updateQR(payload);
      if (qr.status === 200) {
        dispatch(success(qr?.data?.message || 'QR Code updated successfully.'));
        return qr?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const getTrackingPhoneNumbers = async (payload) => {
    try {
      const phoneNumbers = await dispatch(
        fetchPhoneTrackingNumbers({
          pagination: {
            page: payload?.page,
            pageSize: payload?.pageSize,
          },
        })
      );
      if (phoneNumbers.status === 200) {
        return phoneNumbers?.data?.data;
      }
      return null;
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const lookupTrackingPhoneNumbers = async (payload) => {
    try {
      return await dispatch(lookupPhoneNumbers(payload));
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const createTrackingPhone = async (payload) => {
    try {
      const response = await dispatch(createPhoneTrackingNumber(payload));
      if (response?.status === 200 || response?.status === 201) {
        dispatch(success(response?.data?.message || 'Tracking Phone Number created successfully.'));
      }
      return response;
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const destroyTemplateBuilder = async () => {
    await templateBuilderInstanceRef.current?.destroy?.();
    templateBuilderInstanceRef.current = null;
  };

  const onCreateCustomTemplateQuery = async (payload) => {
    setIsChargeDialogOpen(true);
    setDesignerQueryPayload(payload?.data);
  };

  const duplicateCurrentTemplate = async (title) => {
    if (!id) {
      dispatch(failure(MESSAGES.TEMPLATE.DUPLICATE_TEMPLATE_REQUIRED));
      return false;
    }
    const trimedTitle = title.trim();
    const errorText = !trimedTitle
      ? MESSAGES.TEMPLATE.NAME_REQUIRED
      : trimedTitle.length > 50
        ? MESSAGES.TEMPLATE.NAME_LESS_50
        : '';
    if (errorText) {
      dispatch(failure(errorText));
      return false;
    }

    try {
      const response = await doublicateTemplate(id, {
        title: trimedTitle,
      });

      if (response.status === 200) {
        const duplicatedTemplateId = response?.data?.data?.id;
        if (duplicatedTemplateId) {
          setDuplicateTemplateId(duplicatedTemplateId);
          setConfirmDuplicatModal(true);
          return true;
        }
      } else {
        dispatch(failure(response.data.message));
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Unable to clone the template.'));
    }

    return false;
  };

  const openDupicateTemplate = () => {
    if (duplicateTemplateId) {
      navigate(`/template-builder/${duplicateTemplateId}`);
      window.location.reload();
    }
  };

  const handleCloneTemplate = async () => {
    setIsCloneModalLoading(true);
    const cloned = await duplicateCurrentTemplate(cloneTemplateName);
    setIsCloneModalLoading(false);

    if (cloned) {
      setIsCloneModalOpen(false);
      setCloneTemplateName('');
    }
  };

  const getSdkSaveDialog = (root = document) =>
    Array.from(root.querySelectorAll('.confirm-modal-v2')).find((dialog) =>
      dialog
        .querySelector('.confirm-modal-header')
        ?.textContent?.includes('Confirm Save Template')
    );

  const closeHiddenSdkSaveDialog = (root = document) => {
    const sdkSaveDialog = getSdkSaveDialog(root);

    sdkSaveDialog?.querySelector('.confirm-modal-footer > button:last-child')?.click();
  };

  const handleSdkSaveFailure = () => {
    clearTimeout(sdkSaveTimeoutRef.current);
    closeHiddenSdkSaveDialog();
    sdkSaveBridgeActiveRef.current = false;
    document.body.classList.remove('olc-template-save-bridge');
    setIsSaveModalLoading(false);
  };

  const cleanupSdkSavePreflight = () => {
    clearTimeout(sdkSavePreflightTimeoutRef.current);
    sdkSavePreflightActiveRef.current = false;
    document.body.classList.remove('olc-template-save-preflight');
  };

  const getHiddenSdkReuseDialog = () =>
    Array.from(document.querySelectorAll('#template-builder-container .confirm-modal-v2')).find(
      (dialog) =>
        dialog
          .querySelector('.confirm-modal-body')
          ?.textContent?.includes(
            'Are you sure you want to replace the current template with this one?'
          )
    );

  const closeTemplateReuseModal = () => {
    clearTimeout(sdkReuseTimeoutRef.current);
    getHiddenSdkReuseDialog()?.querySelector('.confirm-modal-footer > button:last-child')?.click();
    document.body.classList.remove('olc-template-reuse-bridge');
    setIsReuseModalLoading(false);
    setIsReuseModalOpen(false);
  };

  const submitTemplate = async (formData, handler) => {
    const saved = await handler(formData);

    if (!saved && sdkSaveBridgeActiveRef.current) {
      handleSdkSaveFailure();
    }

    return saved;
  };

  const acceptProof = async (payload = {}, currentDesignerQuery = designerQueryDetails) => {
    const proofQueryId =
      payload?.customTemplateQueryId ||
      payload?.templateId ||
      getDesignerQueryId(currentDesignerQuery, id);

    if (!proofQueryId) {
      dispatch(failure('Custom template query is required to accept proof.'));
      return false;
    }

    const proofPayload = {
      decision:
        payload?.decision ||
        (currentDesignerQuery?.sourceTemplateId || currentDesignerQuery?.source_template_id
          ? 'replace'
          : 'create_new'),
    };
    proofPayload.replaceOriginal = proofPayload.decision === 'replace';

    if (payload?.comment) {
      proofPayload.comment = payload.comment;
    }

    const response = await acceptTemplateProof(proofQueryId, proofPayload);

    if (response?.status === 200) {
      dispatch(success(response?.data?.message || 'Proof accepted successfully.'));
      redirectToOlc();
      return true;
    }

    dispatch(failure(response?.data?.message || 'Unable to accept proof.'));
    return false;
  };

  const denyProof = async (payload, currentDesignerQuery = designerQueryDetails) => {
    const proofQueryId =
      payload?.get?.('customTemplateQueryId') ||
      payload?.get?.('templateId') ||
      getDesignerQueryId(currentDesignerQuery, id);

    if (!proofQueryId) {
      dispatch(failure('Custom template query is required to deny proof.'));
      return false;
    }

    const response = await denyTemplateProof(proofQueryId, payload);

    if (response?.status === 200) {
      dispatch(success(response?.data?.message || 'Proof denied successfully.'));
      redirectToOlc();
      return true;
    }

    dispatch(failure(response?.data?.message || 'Unable to deny proof.'));
    return false;
  };

  const createTemplateBuilder = (currentTemplateData = null, currentDesignerQuery = null) => {
    const proofQueryId = getDesignerQueryId(currentDesignerQuery || currentTemplateData, id);

    templateBuilderInstanceRef.current?.destroy?.();
    templateBuilderInstanceRef.current = new TemplateBuilderSDK({
      container: document.getElementById('template-builder-container'),
      secretKey: import.meta.env.VITE_APP_PLOTNO_API_KEY,
      publicApiKey: getUserToken(),
      platformName: 'OLC',
      allowPropertyFields: isPropertyAddressEnabled,
      allowSenderFields: true,
      templateGalleryModal: true,
      sandbox: isSandboxMode,
      olcTemplate: currentTemplateData,
      designerQueryAmount,
      allowedAddOns: isPropertyAddressEnabled ? ['gsv', 'property_offer'] : [],
      allowDynamicImage: true,
      onResolveDynamicImageUrl: getDynamicImageProxyUrl,
      allowAdditionalPage: true,
      showQrScanNotificationFields: true,
      defaultQrNotificationEmail: currentDesignerQuery?.ownerEmail || userEmail,
      qrScanNotificationEnabled:
        typeof currentDesignerQuery?.isDefaultPlan === 'boolean'
          ? !currentDesignerQuery.isDefaultPlan
          : isQrScanNotificationEnabled,
      enableDynamicQRCodeUrl: true,
      enablePurlTracking: true,
      purlBaseUrl: import.meta.env.VITE_APP_PURL_BASE_URL,
      env: import.meta.env.VITE_APP_ENV,
      currentTheme: currentTheme,
      templateBuilderRoute: approvalMode ? '/hire-a-designer/proof/:id' : undefined,
      enableHtmlBuilder: false,
      htmlTemplateBuilderRoute: '/html-template-builder/:id?',
      showTemplateTypesPage: true,
      showHireDesignerOption: true,
      showProofActions: approvalMode && Boolean(id),
      proofApprovalMode: approvalMode,
      readOnlyMode: approvalMode,
      proofRevision:
        getDesignerQueryProofRevision(currentDesignerQuery) ||
        getProofRevision(currentTemplateData),
      proofApproval: getProofApprovalData(currentDesignerQuery),
      customTemplateQueryId: proofQueryId,
      onGetBrandingImages: getAllBrandingImages,
      onHireDesignerNavigate: () => navigate('/hire-a-designer'),
      onDeleteBrandingImage: deleteBrandingImage,
      onUploadBrandingImage: uploadBrandingImaage,
      onGetQRCodes: getAllQRCodes,
      onDeleteQRCodes: deleteQRCode,
      onUploadQRCode: uploadQRCode,
      onEditQRCode: updateQRCode,
      onGetPURLs: getAllPurls,
      onCreatePURL: createPurl,
      onEditPURL: updatePurl,
      onUpdatePURLStatus: updatePurlStatus,
      onGetTrackingPhoneNumbers: getTrackingPhoneNumbers,
      onLookupTrackingPhoneNumbers: lookupTrackingPhoneNumbers,
      onCreateTrackingPhoneNumber: createTrackingPhone,
      onDuplicateTemplate: duplicateCurrentTemplate,
      enableTemplatePreview: !approvalMode,
      onGetPreviewContacts: getPreviewContacts,
      onAddPreviewContact: addPreviewContact,
      onReturnAndNavigate: redirectToOlc,
      onGetCustomFields: getCustomFields,
      showCreateCustomFieldOption: true,
      onCreateCustomField: createBuilderCustomField,
      onGetTemplates: getAllTemplatesByTab,
      onGetOneTemplate: approvalMode ? undefined : getTemplateById,
      onAcceptProof: (payload) => acceptProof(payload, currentDesignerQuery),
      onDenyProof: (payload) => denyProof(payload, currentDesignerQuery),
      onPreflightHtmlTemplate: preflightHtmlTemplate,
      onViewProofHtmlTemplate: viewProofHtmlTemplate,
      onGenerateHtmlTemplateProofImages: generateProofImagesHtmlTemplate,
      onUploadTemplateImage: uploadHtmlTemplateImage,
      onSubmitHtmlTemplate: submitHtmlTemplate,
      onSubmit: currentTemplateData
        ? (formData) =>
            submitTemplate(formData, (payload) => onUpdate(payload, currentDesignerQuery))
        : (formData) => submitTemplate(formData, onCreate),
      onCreateCustomTemplateQuery: onCreateCustomTemplateQuery,
    });
  };

  useEffect(() => {
    const container = document.getElementById('template-builder-container');
    const removeDefaultTemplateName = !id ? installDefaultTemplateName(container) : null;
    const removeRealPennedFieldsDefault = installRealPennedFieldsDefault(container);
    const removeSidePanelTabIdentity = installSidePanelTabIdentity(container);

    if (builderMode === 'html') {
      createTemplateBuilder();
      setIsTemplateBuilderLoading(false);
    } else if (id && isCustomTemplate) {
      setEditingDesignerQueryTemplate();
    } else if (id) {
      setEditingTemplate();
    } else {
      setDesignerQueryDetails(null);
      createTemplateBuilder();
      setIsTemplateBuilderLoading(false);
    }

    return () => {
      clearTimeout(sdkSaveTimeoutRef.current);
      clearTimeout(sdkSavePreflightTimeoutRef.current);
      clearTimeout(sdkReuseTimeoutRef.current);
      document.body.classList.remove('olc-template-save-bridge');
      document.body.classList.remove('olc-template-save-preflight');
      document.body.classList.remove('olc-template-reuse-bridge');
      removeDefaultTemplateName?.();
      removeRealPennedFieldsDefault();
      removeSidePanelTabIdentity();
      destroyTemplateBuilder();
    };
  }, [id, isCustomTemplate, builderMode]);

  const handleCloseChargeDialog = (isPurchased = false) => {
    setIsChargeDialogOpen(false);
    isPurchased && setIsPurchasedOpen(true);
  };

  const openSaveModalAfterSdkValidation = () => {
    const container = document.getElementById('template-builder-container');
    const saveButton = Array.from(
      container?.querySelectorAll(
        '.v2-top-navigation-container .actionsBtnWrapper > .basic-button'
      ) || []
    ).find((button) => button.textContent?.trim() === 'Save');

    if (!saveButton) {
      dispatch(failure('Unable to start the template save.'));
      return;
    }

    cleanupSdkSavePreflight();
    sdkSavePreflightActiveRef.current = true;
    allowSdkSaveClickRef.current = true;
    document.body.classList.add('olc-template-save-preflight');
    saveButton.click();

    const waitForSdkSaveDialog = (remainingAttempts = 20) => {
      if (!sdkSavePreflightActiveRef.current) {
        return;
      }

      const sdkSaveDialog = getSdkSaveDialog(container);

      if (sdkSaveDialog) {
        closeHiddenSdkSaveDialog(container);
        cleanupSdkSavePreflight();
        setIsSaveModalOpen(true);
        return;
      }

      if (remainingAttempts > 0) {
        sdkSavePreflightTimeoutRef.current = setTimeout(
          () => waitForSdkSaveDialog(remainingAttempts - 1),
          25
        );
        return;
      }

      cleanupSdkSavePreflight();
    };

    waitForSdkSaveDialog();
  };

  const handleConfirmTemplateSave = () => {
    const container = document.getElementById('template-builder-container');
    const saveButton = Array.from(
      container?.querySelectorAll(
        '.v2-top-navigation-container .actionsBtnWrapper > .basic-button'
      ) || []
    ).find((button) => button.textContent?.trim() === 'Save');

    if (!saveButton) {
      dispatch(failure('Unable to start the template save.'));
      return;
    }

    setIsSaveModalLoading(true);
    sdkSaveBridgeActiveRef.current = true;
    allowSdkSaveClickRef.current = true;
    document.body.classList.add('olc-template-save-bridge');
    saveButton.click();

    const submitSdkSaveDialog = (remainingAttempts = 20) => {
      const sdkSaveDialog = Array.from(container.querySelectorAll('.confirm-modal-v2')).find(
        (dialog) =>
          dialog
            .querySelector('.confirm-modal-header')
            ?.textContent?.includes('Confirm Save Template')
      );
      const sdkSaveButton = sdkSaveDialog?.querySelector(
        '.confirm-modal-footer > button:first-child'
      );

      if (sdkSaveButton) {
        sdkSaveButton.click();

        const monitorSdkSaveValidation = (remainingAttempts = 300) => {
          if (!sdkSaveBridgeActiveRef.current) {
            return;
          }

          if (!sdkSaveButton.disabled) {
            handleSdkSaveFailure();
            return;
          }

          if (remainingAttempts > 0) {
            sdkSaveTimeoutRef.current = setTimeout(
              () => monitorSdkSaveValidation(remainingAttempts - 1),
              100
            );
          }
        };

        sdkSaveTimeoutRef.current = setTimeout(() => monitorSdkSaveValidation(), 1000);
        return;
      }

      if (remainingAttempts > 0) {
        sdkSaveTimeoutRef.current = setTimeout(
          () => submitSdkSaveDialog(remainingAttempts - 1),
          25
        );
        return;
      }

      sdkSaveBridgeActiveRef.current = false;
      document.body.classList.remove('olc-template-save-bridge');
      setIsSaveModalLoading(false);
      dispatch(failure('Unable to open the template save confirmation.'));
    };

    submitSdkSaveDialog();
  };

  const handleConfirmTemplateReuse = () => {
    setIsReuseModalLoading(true);

    const submitSdkReuseDialog = (remainingAttempts = 20) => {
      const sdkReuseDialog = getHiddenSdkReuseDialog();
      const sdkReuseButton = sdkReuseDialog?.querySelector(
        '.confirm-modal-footer > button:first-child'
      );

      if (sdkReuseButton) {
        sdkReuseButton.click();

        const monitorTemplateReuse = (remainingChecks = 300) => {
          if (!getHiddenSdkReuseDialog()) {
            document.body.classList.remove('olc-template-reuse-bridge');
            setIsReuseModalLoading(false);
            setIsReuseModalOpen(false);
            return;
          }

          if (remainingChecks > 0) {
            sdkReuseTimeoutRef.current = setTimeout(
              () => monitorTemplateReuse(remainingChecks - 1),
              100
            );
            return;
          }

          document.body.classList.remove('olc-template-reuse-bridge');
          setIsReuseModalLoading(false);
          dispatch(failure('Unable to finish loading the selected template.'));
        };

        sdkReuseTimeoutRef.current = setTimeout(() => monitorTemplateReuse(), 100);
        return;
      }

      if (remainingAttempts > 0) {
        sdkReuseTimeoutRef.current = setTimeout(
          () => submitSdkReuseDialog(remainingAttempts - 1),
          25
        );
        return;
      }

      document.body.classList.remove('olc-template-reuse-bridge');
      setIsReuseModalLoading(false);
      dispatch(failure('Unable to open the template replacement confirmation.'));
    };

    submitSdkReuseDialog();
  };

  const handleTemplateBuilderClickCapture = (event) => {
    const reuseTemplateButton =
      event.target instanceof Element
        ? event.target.closest('.galleryModal .templateCard-v2 .tempButton')
        : null;

    if (reuseTemplateButton?.textContent?.trim() === 'Use Template') {
      document.body.classList.add('olc-template-reuse-bridge');
      setIsReuseModalOpen(true);
      return;
    }

    const cloneButton =
      event.target instanceof Element
        ? event.target.closest('.v2-top-navigation-container .actionsBtnWrapper > .clone')
        : null;

    if (cloneButton) {
      event.preventDefault();
      event.stopPropagation();
      setCloneTemplateName('');
      setIsCloneModalOpen(true);
      return;
    }

    const actionButton =
      event.target instanceof Element
        ? event.target.closest('.v2-top-navigation-container .actionsBtnWrapper > .basic-button')
        : null;
    const actionLabel = actionButton?.textContent?.trim();

    if (!actionButton || (actionLabel !== 'Cancel' && actionLabel !== 'Save')) {
      return;
    }

    if (actionLabel === 'Save' && allowSdkSaveClickRef.current) {
      allowSdkSaveClickRef.current = false;
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (actionLabel === 'Cancel') {
      setIsCancelModalOpen(true);
    } else {
      openSaveModalAfterSdkValidation();
    }
  };

  return (
    <>
      <div className="templateBuilderPage">
        {isTemplateBuilderLoading && (
          <div className="templateBuilderLoader">
            <Loader />
          </div>
        )}
        <div id="template-builder-container" onClickCapture={handleTemplateBuilderClickCapture} />
        <TemplateGalleryDropdownBridge />
      </div>
      <CreateContactModal
        open={isAddPreviewContactOpen}
        onSuccess={(createdContact) =>
          resolveAddPreviewContact(
            createdContact
              ? mapContactToPreviewContact(createdContact, previewContactOptionsRef.current)
              : null
          )
        }
        handleClose={() => {
          setIsAddPreviewContactOpen(false);
          resolveAddPreviewContact(null);
        }}
      />

      <ChargeDialog
        openModal={isChargeDialogOpen}
        designerQueryPayload={designerQueryPayload}
        handleCloseModal={handleCloseChargeDialog}
      />

      <PurchaseDialog
        openModal={isPurchasedOpen}
        handleCloseModal={() => setIsPurchasedOpen(false)}
      />

      <DuplicateConfirmModal
        handleClose={() => setConfirmDuplicatModal(false)}
        open={confirmDuplicatModal}
        header={MESSAGES.TEMPLATE.CLONE_TEMPLATE.TITLE}
        message={MESSAGES.TEMPLATE.CLONE_TEMPLATE.HEADING}
        handleOk={openDupicateTemplate}
        loading={false}
        successButtonName={MESSAGES.TEMPLATE.CLONE_TEMPLATE.SUBMIT_BUTTON}
        cancelButtonName={MESSAGES.TEMPLATE.CLONE_TEMPLATE.CANCEL_BUTTON}
      />

      <ConfirmModal
        openConfirmModal={{
          open: isCancelModalOpen,
          header: 'Confirm Cancel',
          message:
            'Are you sure you want to cancel creating this template? All unsaved changes will be lost.',
          component: <CloseIcon />,
          containedIcon: true,
          successButtonName: 'Yes',
          cancelButtonName: 'No',
          handleOk: redirectToOlc,
          handleClose: () => setIsCancelModalOpen(false),
          handleCancelBtn: () => setIsCancelModalOpen(false),
        }}
      />

      <ConfirmModal
        openConfirmModal={{
          open: isSaveModalOpen,
          header: 'Confirm Save Template',
          message:
            'Are you sure you want to save this template? The updates provided will be used right away for any orders referencing this template.',
          component: <SaveOutlinedIcon />,
          containedIcon: true,
          loading: isSaveModalLoading,
          successButtonName: 'Save',
          cancelButtonName: 'Cancel',
          handleOk: handleConfirmTemplateSave,
          handleClose: () => {
            if (!isSaveModalLoading) {
              setIsSaveModalOpen(false);
            }
          },
          handleCancelBtn: () => {
            if (!isSaveModalLoading) {
              setIsSaveModalOpen(false);
            }
          },
        }}
      />

      <OlcFormModal
        open={isCloneModalOpen}
        onClose={() => {
          if (!isCloneModalLoading) {
            setIsCloneModalOpen(false);
          }
        }}
        title="Clone Template"
        className="templateBuilderCloneModal"
        footer={
          <>
            <OlcButton
              variant="secondary"
              type="button"
              disabled={isCloneModalLoading}
              onClick={() => setIsCloneModalOpen(false)}
            >
              Cancel
            </OlcButton>
            <OlcButton
              variant="primary"
              type="submit"
              form="clone-template-form"
              disabled={isCloneModalLoading}
            >
              {isCloneModalLoading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '20px !important',
                    height: '20px !important',
                  }}
                />
              ) : (
                'Clone'
              )}
            </OlcButton>
          </>
        }
      >
        <form
          id="clone-template-form"
          className="templateBuilderCloneForm"
          onSubmit={(event) => {
            event.preventDefault();
            handleCloneTemplate();
          }}
          noValidate
        >
          <InputLabel htmlFor="clone-template-name" className="templateBuilderCloneLabel">
            Template Name
          </InputLabel>
          <TextField
            id="clone-template-name"
            type="text"
            fullWidth
            autoFocus
            value={cloneTemplateName}
            onChange={(event) => setCloneTemplateName(event.target.value)}
            placeholder="Enter new template name"
          />
          <p className="templateBuilderCloneNote">
            <strong>Note:</strong> Only saved changes will appear in the cloned template.
          </p>
        </form>
      </OlcFormModal>

      <ConfirmModal
        openConfirmModal={{
          open: isReuseModalOpen,
          className: 'templateReuseConfirmModal',
          header: 'Confirm',
          message:
            'Are you sure you want to replace the current template with this one? Any unsaved changes will be lost. Save your changes first or click Yes to proceed.',
          component: <SyncAltIcon />,
          containedIcon: true,
          loading: isReuseModalLoading,
          successButtonName: 'Yes',
          cancelButtonName: 'Cancel',
          handleOk: handleConfirmTemplateReuse,
          handleClose: () => {
            if (!isReuseModalLoading) {
              closeTemplateReuseModal();
            }
          },
          handleCancelBtn: () => {
            if (!isReuseModalLoading) {
              closeTemplateReuseModal();
            }
          },
        }}
      />
    </>
  );
};

export default TemplateBuilder;
